import React, { createRef, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { Store__System } from "@talentbait/shared-components";

import logger from "../../../utils/LoggerFrontend";
import { withRefluxStore, StatusStore } from "../../../Stores";
import * as sc from "./CreativesPerformanceTableReport.styled";

import {
  isMetricPerformanceGood,
  isMetricPerformanceBad,
  isMetricPerformanceAverage
} from "./metricsAnalysisReport";
import {
  calculateActiveDays,
  createComponentData
} from "./CreativesPerformanceTableReport.service";
import Actions from "../../../Actions";
import { formatIntlNumber } from "../../../utils/Helpers";
import { getCTRBarWidth } from "./CreativesPerformanceTableReport.utils";
import { IconCheck } from "../../../Components/Icons/Check";

const TabCreativesPerformanceReport = ({
  channelCampaigns = [],
  isCampaingFinished,
  campaignData,
  userProfile
}) => {
  const { t } = useTranslation();

  const [defaultSortOrder, setDefaultSortOrder] = useState(-1);
  const [defaultSortingField, setDefaultSortingField] = useState("ctr");
  const [metrics, setMetrics] = useState([]);

  const refForScreenShot = createRef(null);

  const { campaignDuration = 30 } = campaignData;
  const { startDate, endDate, activeDays, scale, highestImpressionValue, creativesMetricsSorted } =
    createComponentData(channelCampaigns, defaultSortingField, defaultSortOrder);

  useEffect(() => {
    if (!creativesMetricsSorted) return;

    Actions.getAggregatedCreativeMetricsByHash.completed.listen((result) => {
      logger.log(
        "channelCampaigns:getAggregatedCreativeMetricsByHash",
        "getting getAggregatedCreativeMetricsByHash",
        result
      );
      setMetrics(result);
    });

    Actions.getAggregatedCreativeMetricsByHash.failed.listen((error) => {
      logger.log(
        "error:channelCampaigns:getAggregatedCreativeMetricsByHash",
        "error getting getAggregatedCreativeMetricsByHash ",
        error
      );
      setMetrics(creativesMetricsSorted);
    });

    Actions.getAggregatedCreativeMetricsByHash(creativesMetricsSorted);
  }, []);

  logger.log("channelCampaigns:creatives_Performance", "called", {
    channelCampaigns,
    startDate,
    endDate,
    activeDays,
    highestImpressionValue,
    creativesMetricsSorted,
    scale,
    metrics
  });

  if (!metrics || metrics.length === 0) return <></>;

  return (
    <sc.MainWrapper className="creative-metrics">
      <div>
        {activeDays < 7 && (
          <sc.ActiveDays>
            ⚠️ We gathered insufficient data for these creatives, wait until the learning phase is
            over
          </sc.ActiveDays>
        )}
      </div>

      <div ref={refForScreenShot}>
        <br />
        <sc.Table>
          <sc.Thead>
            {metrics.length > 0 && (
              <tr>
                <sc.HeadTd>#</sc.HeadTd>
                <sc.HeadTd style={{ width: 200 }}>{t("Creative")}</sc.HeadTd>

                {isCampaingFinished ? null : (
                  <>
                    <sc.HeadTd style={{ width: 100 }}>{t("Status")}</sc.HeadTd>
                    <sc.HeadTd>{t("Recommendation")}</sc.HeadTd>
                  </>
                )}

                <sc.HeadTd
                  isActive={defaultSortingField == "impressions"}
                  onClick={() => {
                    if (defaultSortingField == "impressions") {
                      setDefaultSortOrder(defaultSortOrder * -1);
                    }

                    setDefaultSortingField("impressions");
                  }}
                >
                  {t("Impressions")}
                </sc.HeadTd>

                <sc.HeadTd
                  isActive={defaultSortingField === "clicks"}
                  onClick={() => {
                    if (defaultSortingField === "clicks") {
                      setDefaultSortOrder(defaultSortOrder * -1);
                    }
                    setDefaultSortingField("clicks");
                  }}
                >
                  {t("Clicks")}
                </sc.HeadTd>

                <sc.HeadTd
                  isActive={defaultSortingField === "results.value"}
                  onClick={() => {
                    if (defaultSortingField === "results.value") {
                      setDefaultSortOrder(defaultSortOrder * -1);
                    }
                    setDefaultSortingField("results.value");
                  }}
                >
                  {t("Results")}
                </sc.HeadTd>

                <sc.HeadTd
                  isActive={defaultSortingField == "inline_link_click_ctr"}
                  onClick={() => {
                    if (defaultSortingField == "inline_link_click_ctr") {
                      setDefaultSortOrder(defaultSortOrder * -1);
                    }
                    setDefaultSortingField("inline_link_click_ctr");
                  }}
                >
                  {t("Click-Through-Rate (CTR)")}
                </sc.HeadTd>
              </tr>
            )}

            {creativesMetricsSorted.length === 0 && (
              <tr>
                <sc.HeadTd>#</sc.HeadTd>
                <sc.HeadTd style={{ width: 200 }}>{t("Creative")}</sc.HeadTd>
                <sc.HeadTd style={{ width: 100 }}>{t("Status")}</sc.HeadTd>
                <sc.HeadTd>
                  {t("Recommendation")} {t("Impressions")} {t("Clicks")} {t("Results")}{" "}
                  {t("Click-Through-Rate (CTR)")}
                </sc.HeadTd>
              </tr>
            )}
          </sc.Thead>

          <tbody>
            {creativesMetricsSorted.map((metrics, index) => {
              const { impressions, inlineClicks, date_start, asset, results } = metrics;
              const insufficientImpressions = parseInt(impressions || 0) < 1000;
              const creativeActiveDays = calculateActiveDays({ date_start, campaignDuration });
              const url = asset.cloudinaryUrl || asset.url;
              const isShutterstockImage = results?.isShutterstockImage === "true";

              return (
                <sc.TableRow
                  key={metrics.adId || asset?.hash}
                  deactivated={insufficientImpressions}
                >
                  <sc.Td>
                    <sc.TdContainer>
                      <span>{index + 1}</span>

                      <sc.CreativeType>
                        {metrics.asset.type === "image" ? <>🏞</> : <>🎥</>}
                      </sc.CreativeType>

                      {isShutterstockImage && (
                        <sc.ShutterstockMark>
                          <img src="/images/shutterstock_logo.svg" alt="shutterstock logo" />
                        </sc.ShutterstockMark>
                      )}
                    </sc.TdContainer>
                  </sc.Td>

                  <sc.Td key={metrics?.asset?.id}>
                    <sc.NoticeWrap id="creativeImage">
                      <sc.PerformanceImg src={url} />

                      <sc.ActiveDays>
                        {creativeActiveDays} {t("Active days on the channels")}
                      </sc.ActiveDays>
                    </sc.NoticeWrap>
                  </sc.Td>

                  {isCampaingFinished ? null : (
                    <>
                      <sc.Td>
                        {insufficientImpressions ? (
                          <sc.BadgeWrapper
                            bg="#ffd36f"
                            data-tip="This ad creative is still gathering data"
                          >
                            ⏳
                          </sc.BadgeWrapper>
                        ) : (
                          <sc.BadgeWrapper bg="#3b7eeb">
                            <IconCheck
                              color="transparent"
                              dataTip="This ad creative has been analyzed"
                              size={24}
                            />
                          </sc.BadgeWrapper>
                        )}
                      </sc.Td>

                      <sc.Td>
                        {isMetricPerformanceGood({ inlineClicks, impressions }) &&
                          !insufficientImpressions && (
                            <sc.GoodPerformance>
                              {t("Excellent! This image is very appealing to the target audience.")}
                            </sc.GoodPerformance>
                          )}

                        {isMetricPerformanceBad({ inlineClicks, impressions }) &&
                          !insufficientImpressions && (
                            <sc.BadPerformance>
                              {t("This image is not very appealing to the target audience.")}
                            </sc.BadPerformance>
                          )}

                        {isMetricPerformanceAverage({ inlineClicks, impressions }) &&
                          !insufficientImpressions && (
                            <sc.AveragePerformance>
                              {t("This image has an average performance with the target group.")}
                            </sc.AveragePerformance>
                          )}
                        {insufficientImpressions && (
                          <sc.GatheringData>
                            {t("This image is still gathering information.")}
                          </sc.GatheringData>
                        )}
                      </sc.Td>
                    </>
                  )}

                  {parseInt(impressions) === highestImpressionValue ? (
                    <sc.StrongTd>
                      {formatIntlNumber(impressions, { language: userProfile?.language })}
                    </sc.StrongTd>
                  ) : (
                    <sc.Td>
                      {formatIntlNumber(impressions, { language: userProfile?.language })}
                    </sc.Td>
                  )}

                  <sc.Td>{parseFloat(metrics.inlineClicks || 0)}</sc.Td>

                  <sc.Td>
                    <div>{metrics.results.value || 0}</div>
                    <div style={{ color: "lightgray" }}>
                      {metrics.results.value === 1
                        ? metrics.results.fieldText.replace(/s$/, "")
                        : metrics.results.fieldText}
                    </div>
                  </sc.Td>

                  <sc.Td>
                    <sc.Bar
                      width={getCTRBarWidth(scale, metrics.inline_link_click_ctr)}
                      learningPhase={insufficientImpressions}
                    />

                    <sc.BarLabel title={metrics.inline_link_click_ctr + "%"}>
                      {(parseFloat(metrics.inline_link_click_ctr) || 0).toFixed(2)}%
                    </sc.BarLabel>
                  </sc.Td>
                </sc.TableRow>
              );
            })}

            {creativesMetricsSorted.length === 0 && (
              <sc.TableRow>
                <sc.Td>
                  <span>1</span>
                </sc.Td>

                <sc.Td style={{ padding: 10, textAlign: "right" }}>
                  <sc.NoticeWrap>
                    <sc.PerformanceImg src="https://media.licdn.com/dms/image/C4D0BAQG3P2grKKqy7A/company-logo_200_200/0/1581511654557?e=2147483647&v=beta&t=goYRaPwvdvaFPlKsvpBqSu6E5yafRbF5iaOaEnzZuOw"></sc.PerformanceImg>
                  </sc.NoticeWrap>
                </sc.Td>

                <sc.Td style={{ padding: 10, minWidth: "40", textAlign: "center" }}>
                  <sc.BadgeWrapper bg="#ffd36f" data-tip="This ad creative is still gathering data">
                    ⏳
                  </sc.BadgeWrapper>
                </sc.Td>

                <sc.Td style={{ textAlign: "center" }}>
                  <sc.GatheringData>
                    Die Bilder Ihrer Kampagne befinden sich aktuell noch in der Lernphase und lassen
                    zurzeit noch keine aussagekräftige Einschätzung zur Performance zu. Kommen Sie
                    zu einem späteren Zeitpunkt wieder und sehen Sie die Bildmetriken ein.
                  </sc.GatheringData>
                </sc.Td>
              </sc.TableRow>
            )}
          </tbody>
        </sc.Table>
      </div>
      <ReactTooltip effect="solid" />
    </sc.MainWrapper>
  );
};

export default withRefluxStore(
  withRouter(TabCreativesPerformanceReport),
  [StatusStore, Store__System],
  ["campaignData", "adsets", "channelCampaigns", "userProfile"]
);
